import React, { useState } from 'react';
import ScheduleAppointmentPresenter from './ScheduleAppointmentPresenter';
import { PresenterState } from './PresenterState';
import { CernerAppointmentDetails, HaveMRN } from '../../api/models';
import { useAppContext, useSchedulingContext } from '../context';
import {
  CreateUnauthenticatedAppointmentApiService,
  CreateUnauthenticatedAppointmentByMHNApiService,
  CreateUnauthenticatedAppointmentByMHNRequest,
  CreateUnauthenticatedAppointmentByMHNResponse,
  CreateUnauthenticatedAppointmentRequest,
  CreateUnauthenticatedAppointmentResponse,
} from '../../api/services';

const ScheduleAppointmentWrapper: React.FC = () => {
  const [state, setState] = useState<PresenterState>(PresenterState.Intro);
  const [appointmentDetails, setAppointmentDetails] =
    useState<CernerAppointmentDetails>({});
  const [errorCode, setErrorCode] = useState<string | undefined>(undefined);

  const schedulingDetails = useSchedulingContext();
  const { appointmentScheduled } = useAppContext();

  const onContinueIntro = () => {
    setState(PresenterState.Wizard);
  };

  const onWizardProgressed = () => {
    setErrorCode(undefined);
  };

  const onSubmitAppointment = () => {
    const makeCall = async () => {
      setState(PresenterState.ScheduleAppointment);

      let response:
        | CreateUnauthenticatedAppointmentResponse
        | CreateUnauthenticatedAppointmentByMHNResponse;

      if (schedulingDetails.patientDetails?.haveMRN === HaveMRN.Yes) {
        const request: CreateUnauthenticatedAppointmentByMHNRequest = {
          patientMHN: schedulingDetails.patientDetails.demographics?.mrn,
          dateOfBirth:
            schedulingDetails.patientDetails?.demographics?.dateOfBirth ??
            undefined,
          contactInfo: {
            emailAddress:
              schedulingDetails.contactInfoDetails?.patientContactDetails
                .emailAddress,
            homePhoneNumber:
              schedulingDetails.contactInfoDetails?.patientContactDetails
                .phoneNumber,
            mobilePhoneNumber:
              schedulingDetails.contactInfoDetails?.patientContactDetails
                .mobileNumber,
          },
          slotId: schedulingDetails.dateTimeDetails?.slot?.slotId,
          reminderContactMethod:
            schedulingDetails.reviewAndSubmitDetails?.reminderOption,
          specialRequests:
            schedulingDetails.reviewAndSubmitDetails?.additionalDetails,
        };
        response =
          await CreateUnauthenticatedAppointmentByMHNApiService.execute(
            request
          );
      } else {
        const request: CreateUnauthenticatedAppointmentRequest = {
          patient: {
            address: {
              addressLine1:
                schedulingDetails.contactInfoDetails?.patientContactDetails
                  .mailingAddress,
              addressLine2:
                schedulingDetails.contactInfoDetails?.patientContactDetails
                  .additionalAddress,
              city: schedulingDetails.contactInfoDetails?.patientContactDetails
                .city,
              state:
                schedulingDetails.contactInfoDetails?.patientContactDetails
                  .state,
              zipCode:
                schedulingDetails.contactInfoDetails?.patientContactDetails.zip,
            },
            contactInfo: {
              emailAddress:
                schedulingDetails.contactInfoDetails?.patientContactDetails
                  .emailAddress,
              homePhoneNumber:
                schedulingDetails.contactInfoDetails?.patientContactDetails
                  .phoneNumber,
              mobilePhoneNumber:
                schedulingDetails.contactInfoDetails?.patientContactDetails
                  .mobileNumber,
            },
            dateOfBirth:
              schedulingDetails.patientDetails?.demographics?.dateOfBirth ??
              undefined,
            name: {
              firstName:
                schedulingDetails.patientDetails?.demographics?.firstName,
              lastName:
                schedulingDetails.patientDetails?.demographics?.lastName,
              middleName:
                schedulingDetails.patientDetails?.demographics?.middleName,
              suffix:
                schedulingDetails.patientDetails?.demographics?.nameSuffix,
            },
            preferredSex:
              schedulingDetails.patientDetails?.demographics?.preferredSex,
          },
          guardian: undefined,
          slotId: schedulingDetails.dateTimeDetails?.slot?.slotId,
          reminderContactMethod:
            schedulingDetails.reviewAndSubmitDetails?.reminderOption,
          specialRequests:
            schedulingDetails.reviewAndSubmitDetails?.additionalDetails,
        };

        response = await CreateUnauthenticatedAppointmentApiService.execute(
          request
        );
      }
      if (response.succeeded && response.data && response.data.success) {
        setState(PresenterState.AppointmentScheduled);
        setAppointmentDetails(response.data.cernerAppointmentDetail);
        appointmentScheduled();
      } else {
        setState(PresenterState.SchedulingError);
        setErrorCode(response.data?.statusCode.toString() ?? 'Unknown Error');
      }
    };

    makeCall();
  };

  return (
    <>
      <ScheduleAppointmentPresenter
        state={state}
        appointmentDetails={appointmentDetails}
        errorCode={errorCode}
        onContinueIntro={onContinueIntro}
        onSubmitAppointment={onSubmitAppointment}
        onWizardProgressed={onWizardProgressed}
      />
    </>
  );
};

export default ScheduleAppointmentWrapper;
